import { v4 as uuid } from 'uuid';
import { epoch, formatEpochAsDate, utc } from '../../util/datetime';
import { toUUID } from '../../util/transform';
import type { IContactPersonEmployment } from './ContactPersonEmployment.interface';
import Address from '../address/Address';
import ContactPersonEmploymentSubTypes from './ContactPersonEmploymentSubTypes';
import ContactPersonEmploymentCode from './ContactPersonEmploymentCode';
import { ContactEmploymentBasis } from './ContactEmploymentBasis.enum';
import { ContactEmploymentClass } from './ContactEmploymentClass.enum';
import { ContactEmploymentPriority } from './ContactEmploymentPriority.enum';
import { ContactEmploymentType } from './ContactEmploymentType.enum';
import { PersonalTitle } from '../global/PersonalTitle.enum';

export default class ContactPersonEmployment implements IContactPersonEmployment {
  id: string;
  status: ContactEmploymentPriority;
  type: ContactEmploymentType;
  subTypes: ContactPersonEmploymentSubTypes;
  basis: ContactEmploymentBasis;
  role: string;
  employerName: string;
  employerAddress: Address;
  employerContactTitle: PersonalTitle;
  employerContactFirstName: string;
  employerContactSurname: string;
  employerPhoneCode: string;
  employerPhone: string;
  employerType: boolean;
  employerAbn: string;
  employerAcn: string;
  startDate: number;
  endDate: number;
  absOccupationCode: ContactPersonEmploymentCode;
  anzscoIndustryCode: ContactPersonEmploymentCode;
  employmentClass: ContactEmploymentClass;
  probationExpiryDate: number;
  onProbation: boolean;
  isCurrent: boolean;

  getEmployerName() {
    return this.employerName;
  }

  getEmploymentStartDate(timeZone, locale, localeFormat) {
    return this?.startDate !== 0
      ? formatEpochAsDate(this?.startDate, timeZone, locale, localeFormat)
      : '-';
  }

  getEmploymentEndDate(timeZone, locale, localeFormat) {
    return this?.endDate !== 0
      ? formatEpochAsDate(this?.endDate, timeZone, locale, localeFormat)
      : '-';
  }

  static build = (data: IContactPersonEmployment) => new ContactPersonEmployment(data);
  static blank = (isCurrent) => new ContactPersonEmployment({ id: uuid(), isCurrent });

  /**
   * Constructor
   * @param {IContactPersonEmployment} data
   */
  constructor(data?: IContactPersonEmployment) {
    this.id = toUUID(data?.id) || uuid();
    this.status = ContactEmploymentPriority.of(data?.status?.name || data?.status?.toString());
    this.type = ContactEmploymentType.of(data?.type?.name);
    this.subTypes = data?.subTypes || {};
    this.basis = ContactEmploymentBasis.of(data?.basis?.name || data?.basis?.toString());
    this.role = data?.role || '';
    this.employerName = data?.employerName || '';
    this.employerAddress = new Address(data?.employerAddress);
    this.employerContactTitle = PersonalTitle.of(
      data?.employerContactTitle?.name || data?.employerContactTitle?.toString()
    );
    this.employerContactFirstName = data?.employerContactFirstName || '';
    this.employerContactSurname = data?.employerContactSurname || '';
    this.employerPhoneCode = data?.employerPhoneCode || '';
    this.employerPhone = data?.employerPhone || '';
    this.employerType = data?.employerType === true;
    this.employerAbn = data?.employerAbn || '';
    this.employerAcn = data?.employerAcn || '';
    this.startDate = data?.startDate ? epoch(utc(data?.startDate)) : 0;
    this.endDate = data?.endDate ? epoch(utc(data?.endDate)) : 0;
    this.absOccupationCode = new ContactPersonEmploymentCode(data?.absOccupationCode);
    this.anzscoIndustryCode = new ContactPersonEmploymentCode(data?.anzscoIndustryCode);
    this.employmentClass = ContactEmploymentClass.of(
      data?.employmentClass?.name || data?.employmentClass?.toString()
    );
    this.probationExpiryDate = data?.probationExpiryDate
      ? epoch(utc(data?.probationExpiryDate))
      : 0;
    this.onProbation = data?.onProbation === true;
    this.isCurrent = data?.isCurrent === true;
  }
}
